import { Badge, MantineColor } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { ReturnRequestStatus } from "../../../apis/return";
import { DotPill, Tone } from "../../delivery/atoms";

/**
 * Status tone (DeHUB UI standard, canvas Ord_Rules «в»): green = done, amber = waiting on someone,
 * grey = closed without outcome, red = needs attention. Colour lives only in the dot and the pale
 * background; the text stays dark slate.
 */
export const RETURN_STATUS_TONE: Record<ReturnRequestStatus, Tone> = {
  REQUESTED                 : "amber",
  PROPOSED                  : "amber",
  ACCEPTED                  : "green",
  AWAITING_PICKUP_SCHEDULE  : "amber",
  AWAITING_SHIPMENT_INFO    : "amber",
  IN_TRANSIT                : "amber",
  IN_TRANSIT_EXCEPTION      : "red",
  AWAITING_WAREHOUSE_RECEIPT: "amber",
  AWAITING_RESOLUTION       : "amber",
  REFUND_PENDING            : "amber",
  REJECTED                  : "grey",
  DISPUTED                  : "red",
  COMPLETED                 : "green",
  PARTIALLY_COMPLETED       : "green",
  CANCELLED                 : "grey"
};

interface Props {
  status: ReturnRequestStatus;
  /** Kept for call-site compatibility; the dot pill has one size. */
  size?: "xs" | "sm" | "md" | "lg";
  /** Optional color override map (buyer surface). When supplied, the legacy
   *  Mantine Badge renders with that palette instead of the dot pill. */
  colorMap?: Partial<Record<ReturnRequestStatus, MantineColor>>;
}

export function ReturnStatusBadge ({ status, size = "sm", colorMap }: Props){
  const { t } = useTranslation("returns");
  const label = t(`status.${status}`, { defaultValue: "-" });
  if (colorMap) {
    return (
      <Badge color={colorMap[status] ?? "gray"} variant="light" size={size} radius="sm">
        {label}
      </Badge>
    );
  }
  return <DotPill tone={RETURN_STATUS_TONE[status] ?? "grey"}>{label}</DotPill>;
}
